import { DatePipe, NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, OnInit } from '@angular/core';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CollectionCaseCompositeEntity } from '@nk/data-entities-lib';
import { ButtonDirective } from '@nk/ui-lib';
import { DateFormats, RISK_COLOR_CLASS } from '@nk/utils-lib';
import * as Highcharts from 'highcharts';
import { HighchartsChartModule } from 'highcharts-angular';
import { AccountsNumberPipe } from '../../../../pipes/accounts-number.pipe';
import {
  getMonthsRange,
  getQuantityDataSeries,
  getSumDataSeries,
  getSumInterval,
  QUANTITY_INTERVAL,
} from '../../../../utils/company-payment-remarks.utils';

type ChartType = 'quantity' | 'sum';

@Component({
  selector: 'nk-company-payment-remarks-chart',
  templateUrl: './company-payment-remarks-chart.component.html',
  imports: [NgStyle, TranslateModule, ButtonDirective, HighchartsChartModule],
  providers: [DatePipe, AccountsNumberPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyPaymentRemarksChartComponent implements OnInit {
  public collectionCaseCompositeEntities = input<CollectionCaseCompositeEntity[]>([]);
  public printVersion = input(false);
  public highcharts = Highcharts;
  public chartType: ChartType = 'sum';
  public chartOptions!: Highcharts.Options;
  public columnUpdateFlag = false;

  private _translateService = inject(TranslateService);
  private _datePipe = inject(DatePipe);
  private _accountsNumberPipe = inject(AccountsNumberPipe);

  public ngOnInit(): void {
    this.chartOptions = this.createChartOptions(
      this.chartType,
      this.collectionCaseCompositeEntities(),
    ) as Highcharts.Options;
  }

  public changeChartType(chartType: ChartType): void {
    this.chartType = chartType;
    this.chartOptions = this.createChartOptions(
      this.chartType,
      this.collectionCaseCompositeEntities(),
    ) as Highcharts.Options;
  }

  public createChartOptions(
    chartType: ChartType,
    collectionCaseCompositeEntities: CollectionCaseCompositeEntity[],
  ): unknown {
    const monthsRange = getMonthsRange(collectionCaseCompositeEntities);

    const series =
      chartType === 'sum'
        ? {
            name: this._translateService.instant(
              extractTranslation('company_page.payment_remarks_card.chart.sum'),
            ) as string,
            data: getSumDataSeries(this._accountsNumberPipe, collectionCaseCompositeEntities, monthsRange),
            className: 'highcharts-color-' + RISK_COLOR_CLASS['high'],
          }
        : {
            name: this._translateService.instant(
              extractTranslation('company_page.payment_remarks_card.chart.quantity'),
            ) as string,
            data: getQuantityDataSeries(this._accountsNumberPipe, collectionCaseCompositeEntities, monthsRange),
            className: 'highcharts-color-' + RISK_COLOR_CLASS['high'],
          };

    const tickInterval = chartType === 'quantity' ? QUANTITY_INTERVAL : getSumInterval(series.data);

    return {
      chart: {
        type: 'column',
        panning: {
          enabled: true,
          type: 'x',
        },
        styledMode: true,
        events: {
          load: function (event: { target: { reflow: () => void } }): void {
            setTimeout(() => {
              event.target.reflow();
            }, 200);
          },
        },
      },
      credits: {
        enabled: false,
      },
      rangeSelector: {
        selected: 1,
      },
      title: {
        text: '',
      },
      subtitle: {
        text: '',
      },
      xAxis: {
        categories: monthsRange.map((i) => this._datePipe.transform(i, DateFormats.MonthAndYear)),
        crosshair: true,
      },
      yAxis: {
        min: 1,
        tickInterval,
        title: {
          text: '',
        },
        labels: {
          formatter: (item: { value: number }): string => this._accountsNumberPipe.transform(item.value, 1),
        },
      },
      tooltip: {
        headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
        pointFormat: `<tr>
            <td style="color:{series.color};padding:0">{series.name}: </td>
            <td style="padding-right: 10px"><b> {point.title}</b></td>
          </tr>`,
        footerFormat: '</table>',
        shared: true,
        useHTML: true,
      },
      legend: false,
      plotOptions: {
        series: {
          animation: !this.printVersion(),
        },
        column: {
          pointPadding: 0.2,
          borderWidth: 0,
          minPointLength: 3,
        },
      },
      series: [series],
    };
  }
}
